(() => {
  'use strict';
  if (window.__zfMotionBound) return;
  window.__zfMotionBound = true;
  const html = document.documentElement;
  // The class is set in <head> only when motion is enabled and not reduced.
  const motion = () => html.classList.contains('zf-motion');
  const design = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Scroll reveals ---------- */
  const STAGGER = '.zf-products-track,.zf-catalog-grid,.zf-ingredient-grid,.zf-tip-grid,.zr-review-grid,.zx-card-grid,.zx-product-grid';
  const MEDIA_BOX = '.zf-card-image,.zf-media,.zf-pdp-detail-picture';
  let observer;
  const belowFold = el => el.getBoundingClientRect().top > innerHeight * 0.92;
  const cleanup = (el, cls) => {
    const end = event => {
      if (event.target !== el) return;
      el.classList.remove(cls);
      el.style.removeProperty('--zf-delay');
      el.removeEventListener('animationend', end);
    };
    el.addEventListener('animationend', end);
  };

  function splitHeading(h) {
    if ([...h.childNodes].some(n => n.nodeType === 1 && n.tagName !== 'BR')) return false;
    if (!h.textContent.trim()) return false;
    if (!h.dataset.zfOriginal) h.dataset.zfOriginal = h.innerHTML;
    const words = [];
    h.replaceChildren(...[...h.childNodes].flatMap(node => {
      if (node.nodeType === 1) return [node];
      return node.textContent.split(/(\s+)/).filter(Boolean).map(part => {
        if (/^\s+$/.test(part)) return document.createTextNode(' ');
        const word = document.createElement('span');
        word.textContent = part;
        word.style.display = 'inline-block';
        words.push(word);
        return word;
      });
    }));
    if (!words.length) { h.innerHTML = h.dataset.zfOriginal; return false; }
    const lines = [];
    let top = null;
    words.forEach(word => {
      const t = word.offsetTop;
      if (top === null || Math.abs(t - top) > 4) { lines.push([]); top = t; }
      lines[lines.length - 1].push(word.textContent);
    });
    h.replaceChildren(...lines.map((line, i) => {
      const outer = document.createElement('span'), inner = document.createElement('span');
      outer.className = 'zf-split-line';
      inner.className = 'zf-split-inner';
      inner.style.setProperty('--zf-line', i);
      inner.textContent = line.join(' ');
      outer.append(inner);
      return outer;
    }));
    h.setAttribute('aria-label', h.textContent);
    h.dataset.zfLines = lines.length;
    return true;
  }

  function play(el, delay) {
    const kind = el.dataset.zfReveal;
    if (delay) el.style.setProperty('--zf-delay', delay + 's');
    if (kind === 'split') {
      el.classList.add('zf-split-go');
      setTimeout(() => {
        el.innerHTML = el.dataset.zfOriginal;
        el.removeAttribute('aria-label');
        el.classList.remove('zf-split-go');
        delete el.dataset.zfReveal;
      }, Number(el.dataset.zfLines || 1) * 100 + 900);
      return;
    }
    const pre = kind === 'up' ? 'zf-pre' : 'zf-pre-img';
    const inCls = kind === 'up' ? 'zf-in' : kind === 'img' ? 'zf-in-img' : 'zf-in-fade';
    const go = () => { el.classList.remove(pre); el.classList.add(inCls); cleanup(el, inCls); delete el.dataset.zfReveal; };
    if (el.tagName === 'IMG' && !el.complete) {
      let done = false;
      const once = () => { if (!done) { done = true; go(); } };
      el.addEventListener('load', once, { once: true });
      el.addEventListener('error', once, { once: true });
      setTimeout(once, 1800);
    } else go();
  }

  function prepare(el, kind) {
    if (el.dataset.zfReveal || !belowFold(el)) return;
    if (kind === 'split') { if (!splitHeading(el)) kind = 'up'; }
    el.dataset.zfReveal = kind;
    if (kind === 'up') el.classList.add('zf-pre');
    if (kind === 'img' || kind === 'fade') el.classList.add('zf-pre-img');
    observer.observe(el);
  }

  function initReveals(scope) {
    if (!motion() || design || !('IntersectionObserver' in window)) return;
    const main = document.getElementById('MainContent');
    if (!main) return;
    observer = observer || new IntersectionObserver(entries => {
      const hits = entries.filter(e => e.isIntersecting).map(e => e.target);
      hits.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left || a.getBoundingClientRect().top - b.getBoundingClientRect().top);
      let step = 0;
      hits.forEach(el => {
        observer.unobserve(el);
        play(el, el.dataset.zfStagger ? Math.min(step++ * 0.08, 0.4) : 0);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    const root = scope || main;
    // Sections the merchant asked to keep still: FAQ, The ZOVIRO difference / Meet your everyday essentials, Good things.
    const inHero = el => el.closest('[data-zf-hero],.zf-hero-track,.zf-benefit-ticker,.zf-sticky-atc,dialog,#homepage-section-3,#homepage-section-4,[class*="faq"]');
    root.querySelectorAll(STAGGER).forEach(group => [...group.children].forEach(child => {
      if (child.hidden) return;
      child.dataset.zfStagger = 'true';
      prepare(child, 'up');
    }));
    root.querySelectorAll('h2').forEach(h => {
      if (inHero(h) || h.closest('[data-zf-stagger]') || h.classList.contains('visually-hidden') || h.offsetParent === null) return;
      prepare(h, 'split');
      const next = h.nextElementSibling;
      if (next && !next.matches(STAGGER) && next.offsetHeight < innerHeight * 0.8) { prepare(next, 'up'); next.style.setProperty('--zf-delay', '.2s'); }
    });
    root.querySelectorAll('img').forEach(img => {
      if (inHero(img) || img.closest('[data-zf-stagger],[data-zf-gallery],.z-menu-feature,[data-zf-reveal="up"]') || img.classList.contains('zf-card-hover')) return;
      if ((img.width || img.offsetWidth) < 120) return;
      const box = img.closest(MEDIA_BOX);
      if (box) box.classList.add('zf-reveal-media');
      prepare(img, box ? 'img' : 'fade');
    });
  }

  let resizeTimer;
  addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      document.querySelectorAll('[data-zf-reveal="split"]').forEach(h => { h.innerHTML = h.dataset.zfOriginal; splitHeading(h); });
    }, 200);
  });

  /* ---------- Header condenses on scroll ---------- */
  function initHeader() {
    const ann = document.querySelector('.z-announcement'), header = document.querySelector('.z-header');
    if (!header) return;
    const measure = () => {
      html.style.setProperty('--zf-ann-h', (ann ? ann.offsetHeight : 0) + 'px');
      html.style.setProperty('--zf-header-bottom', Math.max(0, Math.round(header.getBoundingClientRect().bottom)) + 'px');
    };
    let ticking = false, settle;
    const update = () => {
      ticking = false;
      const y = scrollY, on = html.classList.contains('zf-scrolled');
      const menuOpen = !!document.querySelector('.z-header details[open]');
      if (!on && y > 140 && !menuOpen) html.classList.add('zf-scrolled');
      else if (on && y < 60) html.classList.remove('zf-scrolled');
      measure();
      clearTimeout(settle);
      settle = setTimeout(measure, 420);
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    addEventListener('resize', measure);
    update();
  }

  /* ---------- Home hero: direction, first-change flag, swipe ---------- */
  function initHero(hero) {
    if (hero.dataset.zfMotionBound) return;
    hero.dataset.zfMotionBound = 'true';
    let last = Number(hero.dataset.index || 0);
    new MutationObserver(() => {
      const n = Number(hero.dataset.index || 0), count = hero.querySelectorAll('[data-zf-hero-slide]').length;
      if (n === last) return;
      hero.classList.add('zf-hero-live');
      hero.classList.toggle('zf-hero-back', n === (last - 1 + count) % count && count > 2);
      last = n;
    }).observe(hero, { attributes: true, attributeFilter: ['data-index'] });
    let x0 = null, y0 = 0, swiped = false;
    hero.addEventListener('touchstart', e => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; swiped = false; }, { passive: true });
    hero.addEventListener('touchend', e => {
      if (x0 === null) return;
      const t = e.changedTouches[0], dx = t.clientX - x0, dy = t.clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const button = hero.querySelector(dx < 0 ? '[data-zf-hero-next]' : '[data-zf-hero-prev]');
      if (!button) return;
      swiped = true;
      button.click();
      setTimeout(() => { swiped = false; }, 400);
    }, { passive: true });
    hero.addEventListener('click', e => { if (swiped && e.target.closest('a')) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

  /* ---------- Accordions ---------- */
  function initAccordions(scope) {
    (scope || document).querySelectorAll('details').forEach(d => {
      if (d.closest('.z-header') || d.dataset.zfAccordion) return;
      const summary = d.querySelector(':scope > summary');
      if (!summary) return;
      d.dataset.zfAccordion = 'true';
      const icon = [...summary.children].find(c => c.tagName === 'SPAN' && c.textContent.trim() === '+');
      if (icon) { icon.classList.add('zf-plus'); icon.setAttribute('aria-hidden', 'true'); }
      d.classList.add('zf-accordion');
    });
  }
  document.addEventListener('click', event => {
    const summary = event.target.closest('details.zf-accordion > summary');
    if (!summary || !motion()) return;
    const d = summary.parentElement;
    event.preventDefault();
    d._zfAnim?.cancel();
    const start = d.offsetHeight;
    const style = getComputedStyle(d);
    const closing = d.open && !d.classList.contains('zf-closing');
    let end;
    if (closing) {
      end = summary.offsetHeight + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      d.classList.add('zf-closing');
    } else {
      d.classList.remove('zf-closing');
      d.open = true;
      end = d.offsetHeight;
    }
    const anim = d.animate({ height: [start + 'px', end + 'px'] }, { duration: Math.min(450, 220 + Math.abs(end - start) * 0.4), easing: 'cubic-bezier(.22,.61,.36,1)' });
    d._zfAnim = anim;
    anim.onfinish = () => { d._zfAnim = null; if (closing) { d.open = false; d.classList.remove('zf-closing'); } };
    anim.oncancel = () => { d._zfAnim = null; };
  });

  /* ---------- Sticky add-to-bag bar ---------- */
  function initSticky() {
    const bar = document.querySelector('[data-zf-sticky-atc]');
    if (!bar || bar.dataset.bound) return;
    const form = document.getElementById(bar.dataset.form);
    const main = form?.querySelector('button[name=add]');
    const anchor = form?.querySelector('.zf-purchase-bottom') || main;
    if (!main || !anchor) return;
    bar.dataset.bound = 'true';
    document.body.classList.add('zf-has-sticky-atc');
    const button = bar.querySelector('[data-zf-sticky-add]'), label = bar.querySelector('[data-zf-sticky-label]'), price = bar.querySelector('[data-zf-sticky-price]');
    const sync = () => {
      button.disabled = main.disabled && main.dataset.zfState !== 'adding';
      if (main.dataset.zfState) button.dataset.zfState = main.dataset.zfState; else delete button.dataset.zfState;
      const mainLabel = main.querySelector('[data-zf-buy-label]'), mainPrice = main.querySelector('[data-zf-price]');
      if (mainLabel) label.textContent = mainLabel.textContent;
      if (mainPrice && price) price.textContent = mainPrice.textContent;
    };
    new MutationObserver(sync).observe(main, { attributes: true, childList: true, subtree: true, characterData: true });
    sync();
    button.addEventListener('click', () => { if (!main.disabled) main.click(); });
    const setVisible = show => {
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      button.tabIndex = show ? 0 : -1;
    };
    // Recheck on scroll as well: a fast scroll can jump the anchor from below the fold to above it without an intersection change.
    let shown = null, queued = false;
    const check = () => { queued = false; const show = anchor.getBoundingClientRect().bottom < 0; if (show !== shown) { shown = show; setVisible(show); } };
    window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(check); } }, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  }

  function init(scope) {
    initAccordions(scope);
    document.querySelectorAll('[data-zf-hero]').forEach(initHero);
    initSticky();
    initReveals(scope);
  }
  const start = () => { initHeader(); init(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
